import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MagnifyingGlass, ArrowRight, Robot, Article, Users, ChartLineUp } from '@phosphor-icons/react';

const STEPS = [
  { num: '01', title: 'Submit your brand brief', desc: 'Define your target queries, audience and product positioning.' },
  { num: '02', title: 'TMOE matches you with publishers', desc: 'We pair you with publishers whose content ranks in AI answers.' },
  { num: '03', title: 'Publishers create AEO content', desc: 'Structured articles, guides and comparisons built for AI citation.' },
  { num: '04', title: 'Your brand appears in AI answers', desc: 'ChatGPT, Gemini, Perplexity and Claude start referencing you.' },
];

const BENEFITS = [
  { icon: Robot, title: 'AI Visibility', desc: 'Get cited as a trusted source in AI-generated answers across ChatGPT, Gemini, Perplexity and Claude.' },
  { icon: ChartLineUp, title: 'Content That Converts', desc: 'Publisher content structured for both AI engines and human readers drives measurable commerce outcomes.' },
  { icon: Users, title: 'Publisher Network Access', desc: 'Tap into TMOE\'s vetted publisher network with proven reach in your category.' },
];

export default function AEOPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="aeo-page">
      {/* Header */}
      <header className="border-b border-neutral-200">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <button onClick={() => navigate('/')}>
            <img src="/logo.png" alt="TMOE" className="h-9 w-auto object-contain" />
          </button>
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/aeo')} className="text-sm font-medium text-foreground">
              AEO
            </Button>
            <Button variant="ghost" onClick={() => navigate('/login')} className="hover:bg-muted">Login</Button>
            <Button onClick={() => navigate('/register')} className="bg-primary text-primary-foreground hover:bg-primary/90">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-black text-white">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-sm font-medium">
              <MagnifyingGlass size={14} weight="bold" />
              Answer Engine Optimisation
            </div>
            <h1 className="font-heading text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl" data-testid="aeo-hero-title">
              Get Your Brand Found on AI
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-neutral-300 sm:text-xl" data-testid="aeo-hero-subtext">
              TMOE's AEO process helps your content appear in answers on ChatGPT, Gemini, Perplexity and Claude — not just search engines.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="mt-8 bg-[#DC120F] text-white hover:bg-[#b80f0d] px-8 text-base"
              data-testid="aeo-hero-cta"
            >
              Start Your AEO Audit
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* What is AEO */}
      <section className="border-b border-neutral-200" data-testid="aeo-what-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <div className="mx-auto max-w-3xl">
            <h2 className="font-heading text-3xl font-bold tracking-tight mb-8">What is AEO?</h2>
            <div className="space-y-6 text-lg leading-relaxed text-neutral-600">
              <p>
                AI engines like ChatGPT and Gemini now answer buyer questions directly. When someone asks
                "best running shoes under 5000" or "most reliable laptops for students," the AI doesn't
                show a list of links — it gives a structured answer, often citing specific brands and products.
                Brands that structure their content correctly get cited as sources.
              </p>
              <p>
                TMOE helps brands get there through publisher content. We work with a network of publishers
                whose articles are already indexed by AI engines. By structuring that content around your
                brand's target queries — with the right format, comparisons, and narratives — we ensure
                your brand appears where buyers are now looking for answers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-neutral-200 bg-neutral-50" data-testid="aeo-steps-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-12 text-center">How It Works</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step) => (
              <div
                key={step.num}
                className="rounded-xl border border-neutral-200 bg-white p-6 transition-shadow hover:shadow-md"
                data-testid={`aeo-step-${step.num}`}
              >
                <div className="mb-3 inline-flex items-center justify-center rounded-lg bg-black px-3 py-1 text-sm font-bold text-white">
                  {step.num}
                </div>
                <h3 className="mb-2 text-base font-bold">{step.title}</h3>
                <p className="text-sm leading-relaxed text-neutral-500">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="border-b border-neutral-200" data-testid="aeo-benefits-section">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-12 text-center">What You Get</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="rounded-xl border border-neutral-200 p-8 text-center transition-shadow hover:shadow-md">
                  <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#DC120F]/10">
                    <Icon size={28} className="text-[#DC120F]" weight="duotone" />
                  </div>
                  <h3 className="mb-2 text-lg font-bold">{b.title}</h3>
                  <p className="text-sm leading-relaxed text-neutral-500">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-black text-white" data-testid="aeo-cta-section">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center">
          <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Ready to be found on AI?</h2>
          <p className="mx-auto mt-4 max-w-xl text-neutral-400">
            Join the brands already appearing in AI-generated answers through TMOE's publisher network.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            className="mt-8 bg-[#DC120F] text-white hover:bg-[#b80f0d] px-8 text-base"
            data-testid="aeo-bottom-cta"
          >
            Get Started
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-200 py-8 text-center text-sm text-neutral-400">
        <Article size={16} className="inline mr-1 -mt-0.5" />
        TMOE Content-to-Commerce Platform
      </footer>
    </div>
  );
}
